import { FC, useMemo } from 'react';
import { TFunction, useTranslation } from 'react-i18next';
import { ActivitiesTable } from './ActivitiesTable';
import { ACTIVITIES } from '__queries/query-keys';
import ActivityService from '__services/ActivityService';
import { useParams } from 'react-router-dom';
import { createLoggedColumns, createScheduledColumns } from './Table';
import {
  defaultLoggedColumnSettings,
  defaultLoggedPageSettings,
  defaultScheduledColumnSettings,
  defaultScheduledPageSettings,
} from './constants';

export const TABS = {
  scheduled: 'scheduled',
  logged: 'logged',
};

const getScheduledQueryKey = (activityObjectId: string, criteria: any) => {
  return ACTIVITIES.SCHEDULED_ACTIVITY(
    activityObjectId,
    criteria && {
      ...criteria,
      fields: criteria.fields?.map(({ id }: { id?: string }) => id),
    }
  );
};

const getLoggedQueryKey = (activityObjectId: string, criteria: any) =>
  ACTIVITIES.LOGGED_ACTIVITY(
    activityObjectId,
    criteria && {
      ...criteria,
      fields: criteria.fields?.map(({ id }: { id?: string }) => id),
    }
  );

type CustomLayoutSelectedTab = keyof typeof TABS;

const getPropsByTab = (tab: CustomLayoutSelectedTab, t: TFunction) => {
  return {
    [TABS.scheduled]: {
      configPropKey: 'scheduled_config',
      columnsConfigPropKey: 'scheduled_column_config',
      getQueryKey: getScheduledQueryKey,
      getInvalidateQueryKey: (activityObjectId: string) =>
        ACTIVITIES.SCHEDULED_ACTIVITY(activityObjectId),
      fetchActivitiesFunc: ActivityService.v2GetScheduledActivities,
      createColumnsFunc: createScheduledColumns,
      defaultColumnSettings: defaultScheduledColumnSettings,
      title: {
        singular: t('Scheduled Activity'),
        plural: t('Scheduled Activities'),
      },
      defaultPageSettings: defaultScheduledPageSettings,
      canEdit: true,
      showGroupedColumnEditor: false,
    },
    [TABS.logged]: {
      configPropKey: 'responses_config',
      columnsConfigPropKey: 'responses_column_config',
      getQueryKey: getLoggedQueryKey,
      getInvalidateQueryKey: (activityObjectId: string) =>
        ACTIVITIES.LOGGED_ACTIVITY(activityObjectId),
      fetchActivitiesFunc: ActivityService.v2GetLoggedActivities,
      createColumnsFunc: createLoggedColumns,
      defaultColumnSettings: defaultLoggedColumnSettings,
      title: {
        singular: t('Logged Activity'),
        plural: t('Logged Activities'),
      },
      defaultPageSettings: defaultLoggedPageSettings,
      canEdit: true,
      showGroupedColumnEditor: true,
    },
  }[tab];
};

const ScheduledAndLoggedPage: FC<{
  customLayoutSelectedTab: CustomLayoutSelectedTab;
}> = ({ customLayoutSelectedTab }) => {
  const { t } = useTranslation();
  const params: {
    id: string;
  } = useParams();
  const { id: activityObjectId } = params;

  const props = useMemo(
    () => getPropsByTab(customLayoutSelectedTab, t),
    [customLayoutSelectedTab, t]
  );

  return (
    <>
      {customLayoutSelectedTab === TABS.scheduled ||
      customLayoutSelectedTab === TABS.logged ? (
        <ActivitiesTable
          key={customLayoutSelectedTab}
          activityObjectId={activityObjectId}
          {...props}
        />
      ) : null}
    </>
  );
};
export default ScheduledAndLoggedPage;
